import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { IconSearch } from './icons';

/**
 * A check that runs as one call on the server, so it can't show real
 * progress: a spinning ring, what's happening, and the things it reads
 * lighting up in turn so a long check still looks alive. Sits in a `Dialog`.
 */
export const CheckProgress: React.FC<{
  message: React.ReactNode;
  /** What the check reads, shown as chips that light up in turn. */
  steps: readonly string[];
  icon?: React.ReactNode;
  /** A quiet last line, such as whether the dialog can be closed. */
  note?: React.ReactNode;
}> = ({ message, steps, icon, note }) => {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = window.setInterval(
      () => setActive((i) => (i + 1) % steps.length),
      1100,
    );
    return () => window.clearInterval(id);
  }, [steps.length]);
  return (
    <div className="flex flex-col items-center gap-4 py-4 text-center">
      <span
        aria-hidden
        className="relative flex h-14 w-14 items-center justify-center"
      >
        <span className="absolute inset-0 rounded-full border-2 border-v2-line" />
        <span className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-v2-accent" />
        <span className="text-v2-accent">
          {icon ?? <IconSearch size={20} />}
        </span>
      </span>
      <p role="status" aria-live="polite" className="text-v2-13 text-v2-fg-2">
        {message}
      </p>
      <ul aria-hidden className="flex flex-wrap justify-center gap-1.5">
        {steps.map((step, i) => (
          <li
            key={step}
            className={clsx(
              'rounded-full border px-2.5 py-1 text-v2-12 transition-colors duration-300',
              i === active
                ? 'border-v2-accent/40 bg-v2-accent/[0.08] text-v2-accent'
                : 'border-v2-line text-v2-fg-3',
            )}
          >
            {step}
          </li>
        ))}
      </ul>
      <div
        aria-hidden
        className="v2-scan h-1 w-full max-w-[280px] overflow-hidden rounded-full bg-v2-sunken"
      >
        <span className="block h-full w-1/3 rounded-full bg-v2-accent" />
      </div>
      {note && <p className="text-v2-12 text-v2-fg-4">{note}</p>}
    </div>
  );
};
